import { t } from '../i18n';
import { computed, reactive, ref } from 'vue';
import { MEDIA_STATUS_POLL_MS, type MediaAssetRecord } from '@codot/contracts';
import { mediaApi } from '../api/content.api';
import { errorMessage } from '../api/http';
import { uploadMedia } from '../api/media-upload.api';
import { toast } from './toast';
import { publishNow, quietUploads } from './publish-intent';

/**
 * Uploads in the background, Google Drive style: one entry per file, from upload to "ready to publish". App-wide
 * (module state), so leaving the Content page doesn't cancel anything; <UploadTray> in the app shell shows them.
 *
 *  uploading  → bytes going up (XHR progress)
 *  optimizing → the server's background worker normalizes it for players (the file is already in Content, but
 *               can't be published until it's READY); polled until READY or FAILED
 *  ready / error / cancelled
 */
export type UploadStage = 'uploading' | 'optimizing' | 'ready' | 'error' | 'cancelled';
export interface UploadEntry {
  key: string;
  name: string;
  size: number;
  /** 0..1 of the bytes. */
  sent: number;
  stage: UploadStage;
  /** Server-side optimizing progress, 0..100 (null while waiting for the worker). */
  optimize: number | null;
  error: string | null;
  asset: MediaAssetRecord | null;
  abort: () => void;
}

export const uploads = reactive<UploadEntry[]>([]);
/** Bumped whenever an entry changes stage: lists showing media reload on it. */
export const uploadsVersion = ref(0);
export const uploading = computed(() => uploads.some((u) => u.stage === 'uploading'));
export const busy = computed(() => uploads.some((u) => u.stage === 'uploading' || u.stage === 'optimizing'));

const bump = () => uploadsVersion.value++;

/** Starts each file at once; `reject` is a client-side pre-check (size, quota) that fails the entry immediately. */
export function startUploads(files: File[], folderId: string | null, reject: (f: File) => string | null = () => null): void {
  for (const file of files) {
    const entry = reactive<UploadEntry>({
      key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      size: file.size,
      sent: 0,
      stage: 'uploading',
      optimize: null,
      error: null,
      asset: null,
      abort: () => {},
    });
    uploads.unshift(entry);
    const problem = reject(file);
    if (problem) {
      fail(entry, problem);
      continue;
    }
    const handle = uploadMedia(file, (f) => (entry.sent = f), undefined, folderId);
    entry.abort = () => {
      handle.abort();
      entry.stage = 'cancelled';
      bump();
    };
    handle.promise.then(
      (asset) => {
        entry.asset = asset;
        entry.sent = 1;
        entry.stage = 'optimizing';
        entry.abort = () => {};
        bump();
        void watchOptimizing(entry);
      },
      (err: unknown) => {
        if (entry.stage !== 'cancelled') fail(entry, errorMessage(err));
      },
    );
  }
}

async function watchOptimizing(entry: UploadEntry): Promise<void> {
  while (entry.stage === 'optimizing' && entry.asset) {
    await new Promise((r) => setTimeout(r, MEDIA_STATUS_POLL_MS));
    try {
      const a = await mediaApi.get(entry.asset.id);
      entry.asset = a;
      entry.optimize = a.status === 'PROCESSING' ? (a.progress ?? 0) : null;
      if (a.status === 'READY') {
        entry.stage = 'ready';
        bump();
        // Offer the next step (unless the Publish drawer started it: it's ticked there already).
        if (!quietUploads.delete(entry.key))
          toast(t('publish.readyPublishQ', { name: a.name }), {
            ms: 10_000,
            action: {
              label: t('publish.publishNow'),
              run: () => void (publishNow.value = [{ ref: { kind: 'media', id: a.id }, name: a.name, type: a.kind === 'video' ? 'video' : a.kind === 'image' ? 'image' : 'app', thumbnailUrl: a.thumbnailUrl }]),
            },
          });
      } else if (a.status === 'FAILED') {
        fail(entry, a.processingError ?? t('ui.notOptimized'));
      }
    } catch (e) {
      // Deleted meanwhile (404) or offline: stop watching; the list shows the truth.
      if ((e as { status?: number }).status === 404) fail(entry, t('ui.fileDeleted'));
    }
  }
}

function fail(entry: UploadEntry, message: string): void {
  entry.stage = 'error';
  entry.error = message;
  bump();
}

export function dismissUpload(key: string): void {
  const i = uploads.findIndex((u) => u.key === key);
  if (i >= 0) uploads.splice(i, 1);
}

/** Close the tray: cancels what is still uploading (optimizing continues on the server regardless). */
export function clearUploads(): void {
  for (const u of uploads) if (u.stage === 'uploading') u.abort();
  uploads.splice(0, uploads.length);
}

// Closing the tab mid-upload loses the file: ask first (browsers show their own wording).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (uploading.value) e.preventDefault();
  });
}
